import React, { useState } from "react";
import ClientNavbar from "../layout/ClientNavbar";
import { NavLink } from "react-router-dom";
import {
  FiUser,
  FiChevronDown,
  FiFileText,
  FiSearch,
  FiAlertTriangle,
  FiPlus,
  FiFilter,
  FiX,
  FiPaperclip,
} from "react-icons/fi";

const TABS = [
  { id: "life", label: "Life Insurance" },
  { id: "general", label: "General Insurance" },
];

const EMPTY_TIP =
  "You could try again by selecting a different filter or date range.";

const styles = {
  primary: "#0d9488",
  text: "#1e293b",
  textMuted: "#64748b",
  border: "#e2e8f0",
  bgCard: "#ffffff",
  bgPage: "#f8fafc",
  tableHeaderBg: "#f8fafc",
  emptyBg: "#fafafa",
};

function TableHeaderCell({ children, align = "left" }) {
  return (
    <th
      style={{
        padding: "14px 16px",
        fontWeight: 600,
        fontSize: "12px",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: styles.textMuted,
        backgroundColor: styles.tableHeaderBg,
        borderBottom: `2px solid ${styles.border}`,
        textAlign: align,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}

function EmptyState() {
  return (
    <div
      style={{
        padding: "48px 24px",
        textAlign: "center",
        backgroundColor: styles.emptyBg,
        borderRadius: "12px",
        margin: "0 1px 1px 1px",
        border: `1px solid ${styles.border}`,
      }}
    >
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          backgroundColor: "#fef3c7",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          marginBottom: 16,
        }}
      >
        <FiAlertTriangle size={28} style={{ color: "#d97706" }} />
      </div>
      <div style={{ fontSize: "16px", fontWeight: 600, color: styles.text, marginBottom: 6 }}>
        No data available.
      </div>
      <div style={{ fontSize: "14px", color: styles.textMuted, maxWidth: 400, margin: "0 auto" }}>
        {EMPTY_TIP}
      </div>
    </div>
  );
}

// Modal wrapper
function ModalBox({ title, onClose, children, width = 640 }) {
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1050,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: "100%",
          maxWidth: width,
          maxHeight: "90vh",
          overflow: "auto",
          backgroundColor: "#fff",
          borderRadius: "12px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: "16px 20px",
            backgroundColor: styles.primary,
            color: "#fff",
            fontSize: "18px",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderRadius: "12px 12px 0 0",
          }}
        >
          <span>{title}</span>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "none",
              width: 36,
              height: 36,
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              color: "#fff",
            }}
          >
            <FiX size={20} />
          </button>
        </div>
        <div style={{ padding: "24px" }}>{children}</div>
      </div>
    </div>
  );
}

const modalInput = {
  width: "100%",
  padding: "10px 12px",
  fontSize: "14px",
  border: `1px solid ${styles.border}`,
  borderRadius: "8px",
  outline: "none",
  boxSizing: "border-box",
};
const modalLabel = { display: "block", marginBottom: 6, fontSize: "14px", fontWeight: 500, color: styles.text };
const modalRow = { display: "flex", gap: 16, marginBottom: 16, flexWrap: "wrap" };
const modalField = { flex: "1 1 180px", minWidth: 0 };

export default function Insurance() {
  const [activeTab, setActiveTab] = useState("life");
  const [dateType, setDateType] = useState("issue");
  const [searchPolicy, setSearchPolicy] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [openModal, setOpenModal] = useState(null);

  // Life Insurance form
  const [liPolicyHolder, setLiPolicyHolder] = useState("");
  const [liInsuranceCompany, setLiInsuranceCompany] = useState("");
  const [liPolicyName, setLiPolicyName] = useState("");
  const [liAgencyCode, setLiAgencyCode] = useState("");
  const [liDateOfIssue, setLiDateOfIssue] = useState("");
  const [liRenewalDate, setLiRenewalDate] = useState("");
  const [liPolicyNo, setLiPolicyNo] = useState("");
  const [liPremiumAmount, setLiPremiumAmount] = useState("");
  const [liSumAssured, setLiSumAssured] = useState("");
  const [liTerm, setLiTerm] = useState("");
  const [liPremiumPayTerm, setLiPremiumPayTerm] = useState("");
  const [liPaymentSchedule, setLiPaymentSchedule] = useState("");
  const [liMoneyBack, setLiMoneyBack] = useState("no");
  const [liStatus, setLiStatus] = useState("");
  const [liAccidentalDeathBenefit, setLiAccidentalDeathBenefit] = useState("");
  const [liNominee, setLiNominee] = useState("");
  const [liRelation, setLiRelation] = useState("");
  const [liBalanceUnits, setLiBalanceUnits] = useState("");
  const [liCurrentValue, setLiCurrentValue] = useState("");
  const [liAsOnDate, setLiAsOnDate] = useState("");
  const [liRemark, setLiRemark] = useState("");

  // General Insurance form
  const [giPolicyHolder, setGiPolicyHolder] = useState("");
  const [giInsuranceCompany, setGiInsuranceCompany] = useState("");
  const [giPolicyName, setGiPolicyName] = useState("");
  const [giAgencyCode, setGiAgencyCode] = useState("");
  const [giDateOfIssue, setGiDateOfIssue] = useState("");
  const [giRenewalDate, setGiRenewalDate] = useState("");
  const [giPolicyNo, setGiPolicyNo] = useState("");
  const [giPreviousPolicyNo, setGiPreviousPolicyNo] = useState("");
  const [giPremiumAmount, setGiPremiumAmount] = useState("");
  const [giSumInsured, setGiSumInsured] = useState("");
  const [giTerm, setGiTerm] = useState("");
  const [giPaymentSchedule, setGiPaymentSchedule] = useState("");
  const [giStatus, setGiStatus] = useState("");
  const [giNoClaimBonus, setGiNoClaimBonus] = useState("");
  const [giRemark, setGiRemark] = useState("");

  const inputBase = {
    padding: "8px 12px",
    fontSize: "14px",
    border: `1px solid ${styles.border}`,
    borderRadius: "8px",
    outline: "none",
    minHeight: 40,
  };
  const btnPrimary = {
    backgroundColor: "#10b981",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 18px",
    fontSize: "14px",
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
  };
  
  const btnSecondary = {
    backgroundColor: "#fff",
    color: styles.primary,
    border: `1px solid ${styles.primary}`,
    borderRadius: "8px",
    padding: "10px 16px",
    fontSize: "14px",
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    cursor: "pointer",
  };

  const sumColumnLabel = activeTab === "life" ? "Sum Assured" : "Sum Insured";

  return (
    <>
      <ClientNavbar />
      <div className="page-content" style={{ background: styles.bgPage, minHeight: "100vh" }}>
        {/* Page header */}
        <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: "12px", overflow: "hidden" }}>
          <div
            className="card-body p-4"

          >
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <div>
                <h3 className="text-primary mb-2">Insurance Manager</h3>
                <nav
                  aria-label="breadcrumb"
                  style={{ "--bs-breadcrumb-divider": "none" }}
                >
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <NavLink to="/overview">Dashboard</NavLink> /
                    </li>
                    <li className="breadcrumb-item active">Insurance</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>

        {/* Main card */}
        <div
          style={{
            background: styles.bgCard,
            borderRadius: "12px",
            border: `1px solid ${styles.border}`,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            overflow: "hidden",
          }}
        >
          {/* Filter bar */}
          <div
            style={{
              padding: "20px 24px",
              borderBottom: `1px solid ${styles.border}`,
              backgroundColor: "#fafafa",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 16,
            }}
          >
            <div style={{ position: "relative", width: 220 }}>
              <FiSearch size={16} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: styles.textMuted }} />
              <input
                type="text"
                value={searchPolicy}
                onChange={(e) => setSearchPolicy(e.target.value)}
                placeholder="Search Policy"
                style={{ ...inputBase, width: "100%", paddingLeft: 36 }}
              />
              <FiFilter size={16} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: styles.textMuted }} />
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: "14px", fontWeight: 500, color: dateType === "issue" ? styles.primary : styles.textMuted }}>
                Issue Date
              </span>
              <div
                style={{
                  width: 48,
                  height: 26,
                  borderRadius: 13,
                  background: dateType === "issue" ? "#10b981" : styles.border,
                  position: "relative",
                  cursor: "pointer",
                }}
                onClick={() => setDateType(dateType === "issue" ? "renewal" : "issue")}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: "50%",
                    background: "#fff",
                    position: "absolute",
                    top: 2,
                    left: dateType === "issue" ? 2 : 24,
                    transition: "left 0.2s",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                  }}
                />
              </div>
              <span style={{ fontSize: "14px", fontWeight: 500, color: dateType === "renewal" ? styles.primary : styles.textMuted }}>
                Renewal Date
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <input
                type="text"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                placeholder="DD-MM-YYYY"
                style={{ ...inputBase, width: 130 }}
              />
              <span style={{ color: styles.textMuted, fontSize: "14px" }}>–</span>
              <input
                type="text"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                placeholder="DD-MM-YYYY"
                style={{ ...inputBase, width: 130 }}
              />
            </div>

            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 10 }}>
              <button type="button" style={btnSecondary}>
                <FiUser size={18} /> Select User <FiChevronDown size={14} />
              </button>
              <button type="button" style={btnPrimary} onClick={() => setOpenModal(activeTab)}>
                <FiPlus size={18} /> Add Insurance
              </button>
              <button type="button" style={btnSecondary} onClick={() => alert("Export to Excel will be implemented")}>
                <FiFileText size={16} /> XLS
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", borderBottom: `1px solid ${styles.border}`, paddingLeft: 24, gap: 0 }}>
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: "14px 20px",
                  border: "none",
                  borderBottom: activeTab === tab.id ? `3px solid #10b981` : "3px solid transparent",
                  background: "none",
                  fontSize: "14px",
                  fontWeight: activeTab === tab.id ? 600 : 500,
                  color: activeTab === tab.id ? "#10b981" : styles.textMuted,
                  cursor: "pointer",
                  marginBottom: -1,
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="table-responsive">
            <table className="table mb-0" style={{ fontSize: "14px" }}>
              <thead>
                <tr>
                  <TableHeaderCell>Issue Date</TableHeaderCell>
                  <TableHeaderCell>Policy Holder</TableHeaderCell>
                  <TableHeaderCell>Policy Name</TableHeaderCell>
                  <TableHeaderCell>Policy No.</TableHeaderCell>
                  <TableHeaderCell>Renewal Date</TableHeaderCell>
                  <TableHeaderCell align="right">Premium</TableHeaderCell>
                  <TableHeaderCell align="right">{sumColumnLabel}</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Policy Doc</TableHeaderCell>
                  <TableHeaderCell>Actions</TableHeaderCell>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={10} className="p-0 border-0">
                    <EmptyState />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ----- POPUP: Life Insurance ----- */}
      {openModal === "life" && (
        <ModalBox title="Life Insurance" onClose={() => setOpenModal(null)} width={680}>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Policy Holder:</label>
              <select value={liPolicyHolder} onChange={(e) => setLiPolicyHolder(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Insurance Company:</label>
              <select value={liInsuranceCompany} onChange={(e) => setLiInsuranceCompany(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Policy Name:</label>
              <select value={liPolicyName} onChange={(e) => setLiPolicyName(e.target.value)} style={modalInput}>
                <option value="">Enter / Select Policy Name</option>
              </select>
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Agency Code:</label>
              <input type="text" value={liAgencyCode} onChange={(e) => setLiAgencyCode(e.target.value)} style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Date of Issue:</label>
              <input type="text" value={liDateOfIssue} onChange={(e) => setLiDateOfIssue(e.target.value)} placeholder="DD-MM-YYYY" style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Renewal Date:</label>
              <input type="text" value={liRenewalDate} onChange={(e) => setLiRenewalDate(e.target.value)} placeholder="DD-MM-YYYY" style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Policy No.:</label>
              <input type="text" value={liPolicyNo} onChange={(e) => setLiPolicyNo(e.target.value)} style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Premium Amount:</label>
              <input type="text" value={liPremiumAmount} onChange={(e) => setLiPremiumAmount(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Sum Assured:</label>
              <input type="text" value={liSumAssured} onChange={(e) => setLiSumAssured(e.target.value)} style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Term:</label>
              <input type="text" value={liTerm} onChange={(e) => setLiTerm(e.target.value)} style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Premium Pay Term:</label>
              <input type="text" value={liPremiumPayTerm} onChange={(e) => setLiPremiumPayTerm(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Payment Schedule:</label>
              <select value={liPaymentSchedule} onChange={(e) => setLiPaymentSchedule(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Money Back:</label>
              <div style={{ display: "flex", gap: 16, alignItems: "center", marginTop: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <input type="radio" name="liMoneyBack" checked={liMoneyBack === "yes"} onChange={() => setLiMoneyBack("yes")} style={{ accentColor: styles.primary }} />
                  <span style={{ fontSize: "14px" }}>Yes</span>
                </label>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                  <input type="radio" name="liMoneyBack" checked={liMoneyBack === "no"} onChange={() => setLiMoneyBack("no")} style={{ accentColor: styles.primary }} />
                  <span style={{ fontSize: "14px" }}>No</span>
                </label>
              </div>
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Status:</label>
              <select value={liStatus} onChange={(e) => setLiStatus(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Accidental Death Benefit:</label>
              <input type="text" value={liAccidentalDeathBenefit} onChange={(e) => setLiAccidentalDeathBenefit(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Nominee:</label>
              <input type="text" value={liNominee} onChange={(e) => setLiNominee(e.target.value)} style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Relation:</label>
              <input type="text" value={liRelation} onChange={(e) => setLiRelation(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Balance Units:</label>
              <input type="text" value={liBalanceUnits} onChange={(e) => setLiBalanceUnits(e.target.value)} style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Current Value:</label>
              <input type="text" value={liCurrentValue} onChange={(e) => setLiCurrentValue(e.target.value)} style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>As on Date:</label>
              <input type="text" value={liAsOnDate} onChange={(e) => setLiAsOnDate(e.target.value)} placeholder="DD-MM-YYYY" style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Policy Document:</label>
              <button type="button" style={{ ...btnPrimary, padding: "10px 16px" }}>
                <FiPaperclip size={16} /> Attach File
              </button>
            </div>
          </div>
          <div style={modalRow}>
            <div style={{ width: "100%" }}>
              <label style={modalLabel}>Remark:</label>
              <textarea value={liRemark} onChange={(e) => setLiRemark(e.target.value)} rows={3} style={{ ...modalInput, width: "100%", resize: "vertical" }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-start", marginTop: 24 }}>
            <button type="button" style={btnPrimary} onClick={() => { alert("Life Insurance saved"); setOpenModal(null); }}>Save</button>
            <button type="button" style={{ ...btnSecondary, borderColor: styles.border, color: styles.textMuted }} onClick={() => setOpenModal(null)}>Cancel</button>
          </div>
        </ModalBox>
      )}

      {/* ----- POPUP: General Insurance ----- */}
      {openModal === "general" && (
        <ModalBox title="General Insurance" onClose={() => setOpenModal(null)} width={680}>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Policy Holder:</label>
              <select value={giPolicyHolder} onChange={(e) => setGiPolicyHolder(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Insurance Company:</label>
              <select value={giInsuranceCompany} onChange={(e) => setGiInsuranceCompany(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Policy Name:</label>
              <input type="text" value={giPolicyName} onChange={(e) => setGiPolicyName(e.target.value)} placeholder="Enter / Select Policy Name" style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Agency Code:</label>
              <input type="text" value={giAgencyCode} onChange={(e) => setGiAgencyCode(e.target.value)} style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Date of Issue:</label>
              <input type="text" value={giDateOfIssue} onChange={(e) => setGiDateOfIssue(e.target.value)} placeholder="DD-MM-YYYY" style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Renewal Date:</label>
              <input type="text" value={giRenewalDate} onChange={(e) => setGiRenewalDate(e.target.value)} placeholder="DD-MM-YYYY" style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Policy No.:</label>
              <input type="text" value={giPolicyNo} onChange={(e) => setGiPolicyNo(e.target.value)} style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Previous Policy No:</label>
              <input type="text" value={giPreviousPolicyNo} onChange={(e) => setGiPreviousPolicyNo(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Premium Amount:</label>
              <input type="text" value={giPremiumAmount} onChange={(e) => setGiPremiumAmount(e.target.value)} style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Sum Insured:</label>
              <input type="text" value={giSumInsured} onChange={(e) => setGiSumInsured(e.target.value)} style={modalInput} />
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Term:</label>
              <input type="text" value={giTerm} onChange={(e) => setGiTerm(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Payment Schedule:</label>
              <select value={giPaymentSchedule} onChange={(e) => setGiPaymentSchedule(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
            <div style={modalField}>
              <label style={modalLabel}>Status:</label>
              <select value={giStatus} onChange={(e) => setGiStatus(e.target.value)} style={modalInput}>
                <option value="">Select</option>
              </select>
            </div>
            <div style={modalField}>
              <label style={modalLabel}>No Claim Bonus:</label>
              <input type="text" value={giNoClaimBonus} onChange={(e) => setGiNoClaimBonus(e.target.value)} style={modalInput} />
            </div>
          </div>
          <div style={modalRow}>
            <div style={modalField}>
              <label style={modalLabel}>Policy Document:</label>
              <button type="button" style={{ ...btnPrimary, padding: "10px 16px" }}>
                <FiPaperclip size={16} /> Attach File
              </button>
            </div>
          </div>
          <div style={modalRow}>
            <div style={{ width: "100%" }}>
              <label style={modalLabel}>Remark:</label>
              <textarea value={giRemark} onChange={(e) => setGiRemark(e.target.value)} rows={3} style={{ ...modalInput, width: "100%", resize: "vertical" }} />
            </div>
          </div>
          <div style={{ display: "flex", gap: 12, justifyContent: "flex-start", marginTop: 24 }}>
            <button type="button" style={btnPrimary} onClick={() => { alert("General Insurance saved"); setOpenModal(null); }}>Save</button>
            <button type="button" style={{ ...btnSecondary, borderColor: styles.border, color: styles.textMuted }} onClick={() => setOpenModal(null)}>Cancel</button>
          </div>
        </ModalBox>
      )}
    </>
  );
}
